/*
 * Which rows the author has actually looked at.
 *
 * The image list shows every attachment, and a thumbnail needs the real bytes -
 * there is no thumbnail endpoint, and adding one would mean an image library on
 * the server. Fetching all of them on open would contradict the reasoning the
 * editor preview already follows: "at 5 MB apiece that is a real cost for no
 * benefit". With the ingest API accepting 50 images per article, an eager list
 * could pull tens of megabytes before the author has scrolled to any of it.
 *
 * So a row's bytes are fetched when the row is first scrolled into view.
 * `rootMargin` starts the fetch slightly before it arrives, so a thumbnail is
 * usually there by the time it is looked at.
 *
 * Revealed is STICKY: scrolling a row back out does not un-reveal it. Revoking
 * and re-fetching bytes because a row left the viewport would trade a one-off
 * cost for a repeating one.
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export interface RevealedRows {
  /** Whether this key's row has been seen (or observation is unavailable). */
  isRevealed: (key: string) => boolean;
  /** Ref callback for the row element. Attach with `ref={observe(key)}`. */
  observe: (key: string) => (element: HTMLElement | null) => void;
}

export function useRevealedRows(): RevealedRows {
  /*
   * Without IntersectionObserver everything counts as revealed immediately.
   * That is the honest fallback: the feature is the thumbnail, and deferring is
   * only an optimisation - a browser (or a test environment) that cannot
   * observe should still show the pictures.
   */
  const supported = typeof IntersectionObserver !== 'undefined';
  const [revealed, setRevealed] = useState<ReadonlySet<string>>(new Set());
  const observer = useRef<IntersectionObserver | null>(null);
  const keys = useRef(new WeakMap<Element, string>());
  /*
   * The rows currently on screen, by key.
   *
   * Needed because of ORDER: React attaches refs before effects run, so the
   * first rows attach while there is no observer yet to hand them to, and -
   * their ref callbacks being stable - they never attach a second time. Without
   * this registry for the effect to pick up, those rows are never observed and
   * never reveal, so no thumbnail ever loads.
   */
  const elements = useRef(new Map<string, HTMLElement>());

  useEffect(() => {
    if (!supported) return;

    const instance = new IntersectionObserver(
      (entries) => {
        const seen: string[] = [];
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const key = keys.current.get(entry.target);
          if (key !== undefined) seen.push(key);
          // Once seen, stop watching: the answer cannot change back.
          instance.unobserve(entry.target);
        }
        if (seen.length === 0) return;
        setRevealed((previous) => {
          /*
           * Returning `previous` unchanged when there is nothing new is not an
           * optimisation, it is what stops a loop. An observer delivers an
           * initial entry for every target it is given, so a row that is
           * already revealed reports itself again the moment it is re-observed.
           * A fresh Set each time would be a new state object, so React would
           * re-render, the ref would re-attach, the row would be re-observed,
           * and around again for as long as it stayed on screen.
           */
          if (seen.every((key) => previous.has(key))) return previous;
          const next = new Set(previous);
          for (const key of seen) next.add(key);
          return next;
        });
      },
      { rootMargin: '200px' },
    );

    observer.current = instance;
    for (const element of elements.current.values()) instance.observe(element);

    return () => {
      instance.disconnect();
      observer.current = null;
    };
  }, [supported]);

  /*
   * One ref callback per key, kept across renders. React compares ref callbacks
   * by identity: a new function each render means every row is detached (called
   * with null) and re-attached on every render, which re-observes rows that
   * were deliberately unobserved. Caching them makes attachment happen once.
   */
  const callbacks = useRef(new Map<string, (element: HTMLElement | null) => void>());
  const observe = useCallback((key: string) => {
    const existing = callbacks.current.get(key);
    if (existing) return existing;
    const callback = (element: HTMLElement | null): void => {
      if (!element) {
        // Detached: drop the reference rather than hold the element forever.
        elements.current.delete(key);
        return;
      }
      elements.current.set(key, element);
      keys.current.set(element, key);
      // Null before the effect has run; the effect picks this row up instead.
      observer.current?.observe(element);
    };
    callbacks.current.set(key, callback);
    return callback;
  }, []);

  const isRevealed = useCallback(
    (key: string) => !supported || revealed.has(key),
    [supported, revealed],
  );

  // Memoised so that callers can depend on it: ArticleEditorPage filters its
  // image list on `isRevealed` inside a useMemo keyed on this.
  return useMemo(() => ({ isRevealed, observe }), [isRevealed, observe]);
}
